/**
 * Date-scrubber windowing and the scores-page filters.
 *
 * Its own module because it is all pure and worth testing on its own — the
 * behaviours here (a row that is always the same width; a filter pair that
 * can legitimately select nothing) are easy to state and easy to break, and
 * reaching them through the component would mean standing up the whole app
 * and stubbing the API.
 */
import type { GameDay } from "./types/nba";

/** How many day tabs the scrubber shows at once. */
export const SCRUB_WINDOW = 7;

/**
 * The slice of game days the scrubber renders, always SCRUB_WINDOW wide.
 *
 * The row used to render every day the API returned, which made its width a
 * function of how much history happened to be ingested — fine at exactly five,
 * but it would grow or shrink under the user as the backfill walked further
 * back. Windowing fixes the count so the row is the same size on every date
 * you click.
 *
 * The selected day is centred where there is room. At the ends the window is
 * clamped rather than shortened: picking the oldest or newest date slides the
 * frame up against that edge and still shows SCRUB_WINDOW tabs, instead of
 * trailing off into three.
 *
 * @param days     oldest -> newest, the order the rail renders in
 * @param selected the ISO date in view, or null before the list resolves
 */
export function scrubWindow(days: string[], selected: string | null): string[] {
  // Fewer days than the window means there is nothing to slide and nothing to
  // pad with — the row is simply as wide as the history available.
  if (days.length <= SCRUB_WINDOW) return days;

  const i = selected ? days.indexOf(selected) : -1;
  // Unknown or absent selection defaults to the newest end, which is what the
  // page shows when no date is in the URL.
  if (i === -1) return days.slice(days.length - SCRUB_WINDOW);

  const half = Math.floor(SCRUB_WINDOW / 2);
  const start = Math.max(0, Math.min(i - half, days.length - SCRUB_WINDOW));
  return days.slice(start, start + SCRUB_WINDOW);
}

/**
 * The date one page away in `days`, or null when there is no page that way.
 *
 * A page is SCRUB_WINDOW days, so the arrows move the rail by exactly the
 * number of tabs it shows — press once and every date in the row is a new one.
 * Counted in POSITIONS, not in calendar days: the list is the days the league
 * actually played, already narrowed by the month and team filters, so "back a
 * week" means seven of the dates on offer rather than seven dates on a
 * calendar, most of which would not be here to land on.
 *
 * The last step to either end is short rather than refused. From the third
 * day of a season this returns the very first, not null — clamping keeps the
 * ends reachable, which is the case the arrows exist for.
 *
 * Null means the current date IS that end, and the caller should disable the
 * control rather than render one that does nothing.
 *
 * @param days     oldest -> newest, the order the rail renders in
 * @param selected the ISO date in view, or null before the list resolves
 * @param dir      -1 for older, +1 for newer
 */
export function scrubStep(
  days: string[],
  selected: string | null,
  dir: -1 | 1,
): string | null {
  if (!days.length) return null;

  const i = selected ? days.indexOf(selected) : days.length - 1;
  // A selection that is not in the list — a date reached by URL that the
  // filters exclude — has no position to step from.
  if (i === -1) return null;

  const target = Math.max(0, Math.min(i + dir * SCRUB_WINDOW, days.length - 1));
  return target === i ? null : days[target];
}

/* ------------------------------------------------------------------ *
 * Month and team filters
 * ------------------------------------------------------------------ */

/** A selectable month, keyed the way an ISO date starts. */
export interface MonthOption {
  /** "2026-04" */
  key: string;
  /** "April 2026" */
  label: string;
}

/** Sentinel for "no filter applied", used by both selects. */
export const ALL = "";

/**
 * The months represented in a set of game days, chronological.
 *
 * Derived from the data rather than a fixed calendar so the select never
 * offers a month with nothing behind it — the one case the user cannot
 * recover from without knowing to change it back.
 */
export function monthOptions(days: GameDay[]): MonthOption[] {
  const keys = [...new Set(days.map((d) => d.date.slice(0, 7)))].sort();
  return keys.map((key) => ({ key, label: formatMonth(key) }));
}

/**
 * "2026-04" -> "April 2026".
 *
 * Formats the key itself rather than looking it up among the months that have
 * games, because the case that most needs a label is the one that is absent
 * from that list: a month reached by URL with nothing in it. Looking it up
 * returned null there and the page fell back to a generic "no games
 * available", which is exactly the wrong message for "no games in July".
 */
export function formatMonth(key: string): string {
  const [y, m] = key.split("-");
  const year = Number(y);
  const month = Number(m);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return key;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Game days matching both filters.
 *
 * The two compose rather than override: picking April AND Boston asks for
 * Boston's April games, and getting nothing back is a legitimate answer the
 * caller is expected to render, not an error state to suppress. That is why
 * this returns an empty array rather than falling back to everything —
 * silently ignoring a filter the user set is worse than telling them it
 * matched nothing.
 */
export function filterDays(days: GameDay[], month: string, team: string): GameDay[] {
  return days.filter(
    (d) => (!month || d.date.startsWith(month)) && (!team || d.teams.includes(team)),
  );
}

/** "2026-10-29" for an instant on New York's calendar, built from parts so no locale's date order can creep in. */
function etIso(at: number): string {
  const p = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(new Date(at));
  const part = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** The hour (ET) the league night ends and the next game day starts (DSPRT-609). */
export const NIGHT_ENDS_ET = 6;

/**
 * The calendar date in New York, rolling at midnight: the day the backend
 * files today's games under and the live stream (/scoreboard with no date)
 * serves. Home's "Tonight" is this day (B-279): from midnight its slate is
 * the one still to come, and the night just played is "Last night".
 */
export function etToday(now: number = Date.now()): string {
  return etIso(now);
}

/**
 * The league night, ISO, rolling over at 6 am ET — UTC would call it tomorrow
 * from 8 pm ET, just as the night's games tip. The day Scores opens on, the
 * Today button and the T key go to and the rail marks TODAY (B-284): the one
 * notion of "today" on Scores. `now` so a test can pin it instead of racing
 * the clock.
 */
export function leagueToday(now: number = Date.now()): string {
  // The league's night runs past midnight: a West Coast game tips at 10 pm ET
  // and ends after 1 am, so until 6 am ET the day is still last night's
  // (DSPRT-609). At midnight sharp the Scores page jumped a reader off a game
  // still being played onto tomorrow's schedule.
  return etIso(now - NIGHT_ENDS_ET * 3_600_000);
}

/**
 * The day the Scores rail and its calendar mark TODAY, on the reader's
 * calendar (B-284). Normally the reader's own today (DSPRT-405). Between
 * midnight and 6 am ET the league night is still yesterday's date, and the
 * mark goes on that night's slate, the day Scores opens on and Today goes to;
 * east of New York that slate already falls on the reader's today.
 *
 * @param readerToday the reader's own today (localTodayIso)
 * @param readerDayOf a league date's day on the reader's calendar (slateDay)
 */
export function markedToday(readerToday: string, readerDayOf: (iso: string) => string, now: number = Date.now()): string {
  const night = leagueToday(now);
  return night === etToday(now) ? readerToday : readerDayOf(night);
}

/**
 * What the desk asks the scoreboard for: null for the live stream (today on
 * New York's calendar), else the date to read. Only Scores reads another day;
 * Home and every other page follow the stream (B-279), so Home's tonight is
 * not emptied by Scores' default day after midnight.
 */
export function scoreboardDate(onScores: boolean, selectedDay: string | null, now: number = Date.now()): string | null {
  return onScores && selectedDay && selectedDay !== etToday(now) ? selectedDay : null;
}

/**
 * Whether a date read off the scoreboard is re-read (B-280): the league night
 * after midnight, while a game on it is still being played. A truly past day
 * is read once, whatever a stale row says.
 */
export function rereadsDay(date: string, games: ReadonlyArray<{ state: string }>, now: number = Date.now()): boolean {
  return date >= leagueToday(now) && games.some((g) => g.state === "LIVE");
}

/**
 * Which date should be showing, given the filters.
 *
 * Keeps the requested date when the filters still admit it, so changing month
 * or team does not throw away a date that is still valid. Otherwise picks the
 * closest scheduled game: the first day at or after today, since days arrive
 * oldest-first here. Only when nothing in range is left to play — every
 * remaining day is in the past — does it fall back to the newest of those,
 * which is what this always did before the schedule went in and every date on
 * hand actually was history. Null means nothing matched and the caller should
 * show the empty state.
 */
export function resolveSelectedDay(
  filtered: GameDay[],
  requested: string | null,
  today: string = leagueToday(),
): string | null {
  if (!filtered.length) return null;
  if (requested && filtered.some((d) => d.date === requested)) return requested;
  const upcoming = filtered.find((d) => d.date >= today);
  return (upcoming ?? filtered[filtered.length - 1]).date;
}
